/**
 * Turning a thrown thing into something an operator can act on.
 *
 * D57. Pure functions, so the parts that decide what happens are tested
 * without a DOM — the components that use them are verified by driving the app
 * in a browser instead. Component-level tests would need a testing library and
 * a DOM environment, which is a dependency decision to take deliberately.
 *
 * Nothing here may throw. Every function in this file runs on a path that is
 * already handling a failure, and a failure handler that fails leaves exactly
 * the blank page the whole exercise exists to remove.
 */

/** One thing that went wrong, as the UI needs it. */
export interface Failure {
  /** One short line for a volunteer. Never a stack trace. */
  readonly summary: string;
  /** The underlying text, behind a disclosure. Bounded, so it cannot fill a phone screen. */
  readonly detail: string;
}

/** A `Failure` that reached the global net rather than a boundary. */
export interface RuntimeProblem extends Failure {
  readonly id: string;
  readonly source: 'error' | 'unhandledrejection';
}

const FALLBACK_SUMMARY = 'Something went wrong.';

/** Enough to diagnose, little enough to read on a phone. */
const MAX_DETAIL = 2000;

/**
 * Describe anything at all, including things that resist being described.
 *
 * `String(value)` is not safe here: an object with a throwing `toString`, or a
 * revoked Proxy, throws from the very call meant to report a throw. Everything
 * is wrapped, and the wrapping has a test.
 */
export function describeError(value: unknown): Failure {
  try {
    if (value instanceof Error) {
      const summary = value.message.trim();
      const detail = [value.name, value.message, value.stack ?? ''].filter(Boolean).join('\n');

      return {
        summary: summary === '' ? FALLBACK_SUMMARY : summary,
        detail: clamp(detail),
      };
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      return { summary: trimmed === '' ? FALLBACK_SUMMARY : trimmed, detail: clamp(trimmed) };
    }

    const rendered = safeStringify(value);
    return {
      summary: FALLBACK_SUMMARY,
      detail: clamp(rendered),
    };
  } catch {
    // Describing it failed. Say so honestly rather than propagating.
    return { summary: FALLBACK_SUMMARY, detail: 'The error could not be described.' };
  }
}

function clamp(text: string): string {
  return text.length <= MAX_DETAIL ? text : `${text.slice(0, MAX_DETAIL)}\n… truncated`;
}

function safeStringify(value: unknown): string {
  try {
    const json = JSON.stringify(value);
    if (typeof json === 'string' && json !== '') return json;
  } catch {
    // Circular, or a throwing getter. Fall through.
  }

  try {
    return String(value);
  } catch {
    return 'The error could not be described.';
  }
}

/**
 * Report the same failure once, not a hundred times.
 *
 * A render loop or a retrying effect produces the identical error repeatedly,
 * and a banner per occurrence is design rule 12 in miniature: an operator who
 * has to dismiss forty identical warnings learns to dismiss all of them. The
 * first is shown, and repeats inside the window are dropped.
 *
 * Time is passed in rather than read, so the behaviour is testable without
 * waiting and without a fake clock.
 */
export function createDeduper(windowMs: number): (key: string, now: number) => boolean {
  const lastSeen = new Map<string, number>();

  return (key, now) => {
    const previous = lastSeen.get(key);

    if (previous !== undefined && now - previous < windowMs) {
      // Deliberately not refreshed: a continuous loop should report again once
      // the window passes, rather than being silenced forever by its own noise.
      return false;
    }

    lastSeen.set(key, now);
    return true;
  };
}

/** Distinct enough to deduplicate on, without the stack making every one unique. */
export function keyOf(failure: Failure): string {
  return failure.summary;
}
